import React, { useEffect, useState } from "react";
import API from "../services/api";
import Sidebar from "../components/Sidebar";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

function Settings() {
  const [dashboard, setDashboard] = useState(null);

  const { t, i18n } = useTranslation();

  const [settings, setSettings] = useState({
    notifications_enabled: true,
    weather_alerts: true,
    ai_recommendations_enabled: true,
    language: "English",
    dark_mode: false
  });

  useEffect(() => {
    fetchDashboard();
    fetchSettings();

    const savedLang = localStorage.getItem("app_language");

    if (savedLang) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await API.get("/dashboard/summary");
      setDashboard(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await API.get("/settings");
      setSettings(res.data);

      if (res.data.language) {
        const langMap = {
          English: "en",
          Hindi: "hi",
          Marathi: "mr"
        };

        const langCode =
          langMap[res.data.language] || "en";

        i18n.changeLanguage(langCode);
        localStorage.setItem(
          "app_language",
          langCode
        );
      }
    } catch {
      console.log("No settings found, using defaults");
    }
  };

  const handleToggle = (e) => {
    setSettings({
      ...settings,
      [e.target.name]: e.target.checked
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setSettings({
      ...settings,
      [name]: value
    });

    if (name === "language") {
      const langMap = {
        English: "en",
        Hindi: "hi",
        Marathi: "mr"
      };

      const langCode = langMap[value];

      i18n.changeLanguage(langCode);
      localStorage.setItem(
        "app_language",
        langCode
      );
    }
  };

  const saveSettings = async () => {
    try {
      await API.put("/settings/update", settings);

      toast.success(
        t("settingsSaved") ||
          "Settings saved successfully"
      );
    } catch {
      toast.error(
        t("settingsSaveFailed") ||
          "Failed to save settings"
      );
    }
  };

  const progress =
    dashboard?.total_days > 0
      ? Math.round(
          (dashboard.current_day / dashboard.total_days) * 100
        )
      : 0;

  return (
    <div className="dashboard-layout">
      <Sidebar />

      <div className="main-content">
        <div className="container-fluid">

          {/* Photo Reminder */}
          <div className="card dashboard-card photo-card mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <h5>📷 {t("dailyPhotoReminder")}</h5>
                <p>{t("capturePhoto")}</p>
              </div>

              <button className="btn btn-success">
                {t("takePhoto")}
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row g-4 mb-4">
            <div className="col-md-4">
              <div className="card dashboard-card text-center">
                <h6>{t("currentDay")}</h6>

                <h2>
                  {dashboard?.current_day || 0}/
                  {dashboard?.total_days || 60}
                </h2>

                <small>
                  {(dashboard?.total_days || 60) -
                    (dashboard?.current_day || 0)}{" "}
                  {t("daysRemaining")}
                </small>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card dashboard-card text-center">
                <h6>{t("currentPhase")}</h6>

                <span className="badge bg-primary p-2">
                  {dashboard?.current_phase ||
                    t("notStarted")}
                </span>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card dashboard-card text-center">
                <h6>{t("progress")}</h6>

                <div className="progress mb-2">
                  <div
                    className="progress-bar bg-success"
                    style={{
                      width: `${progress}%`
                    }}
                  ></div>
                </div>

                <h4>{progress}%</h4>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="card dashboard-card">
            <h3 className="mb-4">
              ⚙ {t("settings")}
            </h3>

            <div className="setting-row">
              <label>
                {t("enableNotifications")}
              </label>
              <input
                type="checkbox"
                name="notifications_enabled"
                checked={
                  settings.notifications_enabled
                }
                onChange={handleToggle}
              />
            </div>

            <div className="setting-row">
              <label>{t("weatherAlerts")}</label>
              <input
                type="checkbox"
                name="weather_alerts"
                checked={settings.weather_alerts}
                onChange={handleToggle}
              />
            </div>

            <div className="setting-row">
              <label>
                {t("aiRecommendations")}
              </label>
              <input
                type="checkbox"
                name="ai_recommendations_enabled"
                checked={
                  settings.ai_recommendations_enabled
                }
                onChange={handleToggle}
              />
            </div>

            <div className="setting-row">
              <label>{t("darkMode")}</label>
              <input
                type="checkbox"
                name="dark_mode"
                checked={settings.dark_mode}
                onChange={handleToggle}
              />
            </div>

            <div className="mb-4">
              <label className="form-label">
                {t("language")}
              </label>

              <select
                className="form-select"
                name="language"
                value={settings.language}
                onChange={handleChange}
              >
                <option>English</option>
                <option>Hindi</option>
                <option>Marathi</option>
              </select>
            </div>

            <button
              className="btn btn-success"
              onClick={saveSettings}
            >
              {t("saveSettings")}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default Settings;